/**
 * Execution sidebar — shows agent execution progress with todos and
 * workflow steps rendered as independent sections.
 *
 * - **Todos section** — compact checklist from todowrite tool output.
 *   Shows first 4 items by default; expandable for more.
 * - **Workflow steps section** — structured workflow step list with
 *   progress bar. Independently collapsible.
 */

import { useState } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types — defined locally (workshop cannot import from @/stores/)
// ---------------------------------------------------------------------------

export interface ExecutionTodo {
  id: string;
  title: string;
  status: "pending" | "running" | "completed" | "failed";
}

export interface WorkflowStep {
  index: number;
  label: string;
  status: "pending" | "running" | "completed" | "waiting_approval" | "failed";
  agentKey?: string;
}

/** Overall execution state — drives the header toggle button. */
export type ExecutionState =
  | "idle"
  | "running"
  | "approval"
  | "completed"
  | "failed";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ExecutionSidebarProps {
  executionState: ExecutionState;
  todos: ExecutionTodo[];
  workflowSteps: WorkflowStep[];
  workflowName?: string;
  onRun?: () => void;
  onStop?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}

// ---------------------------------------------------------------------------
// Status icon — per-item status indicator
// ---------------------------------------------------------------------------

type ItemStatus = "pending" | "running" | "completed" | "failed";

function StatusIcon({ status }: { status: ItemStatus }) {
  switch (status) {
    case "completed":
      return (
        <Icons.Check
          className="h-3 w-3 text-emerald-400"
          aria-hidden="true"
        />
      );
    case "running":
      return (
        <Icons.Spinner
          className="h-3 w-3 animate-spin text-blue-400"
          aria-hidden="true"
        />
      );
    case "failed":
      return (
        <Icons.AlertTriangle
          className="h-3 w-3 text-red-400"
          aria-hidden="true"
        />
      );
    case "pending":
    default:
      return (
        <Icons.Circle
          className="h-3 w-3 text-muted"
          aria-hidden="true"
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Header action — toggle button driven by execution state
// ---------------------------------------------------------------------------

function HeaderAction({
  executionState,
  onStop,
  onRun,
  onApprove,
  onReject,
}: {
  executionState: ExecutionState;
  onStop?: () => void;
  onRun?: () => void;
  onApprove?: () => void;
  onReject?: () => void;
}) {
  if (executionState === "approval" && onApprove && onReject) {
    return (
      <span className="flex items-center gap-1">
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded text-emerald-400 transition-colors hover:bg-emerald-400/20"
          onClick={onApprove}
          data-testid="exec-approve-btn"
          aria-label="Approve"
        >
          <Icons.Check className="h-3 w-3" />
        </button>
        <button
          type="button"
          className="flex h-5 w-5 items-center justify-center rounded text-red-400 transition-colors hover:bg-red-400/20"
          onClick={onReject}
          data-testid="exec-reject-btn"
          aria-label="Reject"
        >
          <Icons.X className="h-3 w-3" />
        </button>
      </span>
    );
  }

  if (executionState === "running") {
    return (
      <button
        type="button"
        className="flex h-5 w-5 items-center justify-center rounded text-secondary transition-colors hover:bg-glass-fill-heavy hover:text-primary"
        onClick={onStop}
        data-testid="exec-stop-btn"
        aria-label="Stop"
      >
        <Icons.Square className="h-2.5 w-2.5" />
      </button>
    );
  }

  // idle, completed, failed → play button
  return (
    <button
      type="button"
      className="flex h-5 w-5 items-center justify-center rounded text-secondary transition-colors hover:bg-glass-fill-heavy hover:text-primary"
      onClick={onRun}
      data-testid="exec-run-btn"
      aria-label="Run"
    >
      <Icons.Play className="h-2.5 w-2.5" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Workflow step status normalization
// ---------------------------------------------------------------------------

/** Map WorkflowStep.status to the ItemStatus union used by StatusIcon. */
function normalizeStepStatus(
  status: WorkflowStep["status"],
): ItemStatus {
  if (status === "waiting_approval") return "running";
  return status;
}

// ---------------------------------------------------------------------------
// Todos section
// ---------------------------------------------------------------------------

const INITIAL_VISIBLE = 4;

function TodosSection({ todos }: { todos: ExecutionTodo[] }) {
  const [expanded, setExpanded] = useState(false);
  const visibleTodos = expanded ? todos : todos.slice(0, INITIAL_VISIBLE);
  const hiddenCount = todos.length - INITIAL_VISIBLE;

  return (
    <div data-testid="exec-todos-section">
      {/* Section header */}
      <div className="flex items-center gap-1.5 px-3 py-1.5">
        <Icons.ListTodo className="h-3 w-3 flex-shrink-0 text-muted" />
        <span className="text-[9px] font-medium uppercase tracking-wider text-muted">
          Tasks
        </span>
      </div>

      {/* Todo list */}
      <div className="flex flex-col px-1">
        {visibleTodos.map((todo) => (
          <div
            key={todo.id}
            className={cn(
              "rounded-md px-2 py-1.5",
              todo.status === "running" && "bg-accent/10",
              todo.status === "failed" && "bg-red-400/10",
            )}
            data-testid={`exec-todo-${todo.id}`}
          >
            <div className="flex items-center gap-2">
              <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                <StatusIcon status={todo.status} />
              </span>
              <span
                className={cn(
                  "flex-1 truncate text-[10px]",
                  todo.status === "running"
                    ? "font-medium text-primary"
                    : todo.status === "completed"
                      ? "text-secondary"
                      : todo.status === "failed"
                        ? "text-red-400"
                        : "text-muted",
                )}
              >
                {todo.title}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Expand / collapse toggle */}
      {hiddenCount > 0 && (
        <button
          type="button"
          className="w-full px-3 py-1 text-left text-[9px] text-accent transition-colors hover:text-accent/80"
          onClick={() => setExpanded((prev) => !prev)}
          data-testid="exec-todos-expand-btn"
        >
          {expanded ? "Show less" : `+ ${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow steps section
// ---------------------------------------------------------------------------

function WorkflowStepsSection({
  steps,
  workflowName,
}: {
  steps: WorkflowStep[];
  workflowName: string;
}) {
  const [collapsed, setCollapsed] = useState(false);

  const total = steps.length;
  const completedCount = steps.filter(
    (s) => s.status === "completed",
  ).length;
  const currentStepIndex =
    steps.findIndex((s) => s.status === "running") + 1;
  const progressPercent = total > 0 ? (completedCount / total) * 100 : 0;

  return (
    <div data-testid="exec-workflow-section">
      {/* Section header — clickable to collapse */}
      <button
        type="button"
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left transition-colors hover:bg-glass-fill-heavy/50"
        onClick={() => setCollapsed((prev) => !prev)}
        data-testid="exec-workflow-toggle-btn"
        aria-expanded={!collapsed}
      >
        <Icons.Zap className="h-3 w-3 flex-shrink-0 text-accent" />
        <span className="flex-1 truncate text-[9px] font-medium uppercase tracking-wider text-muted">
          {workflowName}
        </span>
        {collapsed ? (
          <Icons.ChevronRight className="h-3 w-3 text-muted" />
        ) : (
          <Icons.ChevronDown className="h-3 w-3 text-muted" />
        )}
      </button>

      {!collapsed && (
        <>
          {/* Progress bar */}
          <div className="flex flex-col gap-1.5 px-3 pb-1">
            <span className="text-[10px] text-muted">
              {currentStepIndex > 0
                ? `Step ${currentStepIndex} of ${total}`
                : `${completedCount} of ${total} done`}
            </span>
            <div className="h-1 w-full overflow-hidden rounded-full bg-glass-fill-heavy">
              <div
                className="h-full rounded-full bg-accent transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Step list */}
          <div className="flex flex-col px-1">
            {steps.map((step) => {
              const itemStatus = normalizeStepStatus(step.status);
              const isFailed = step.status === "failed";
              const isWaiting = step.status === "waiting_approval";
              return (
                <div
                  key={step.index}
                  className={cn(
                    "rounded-md px-2 py-1.5",
                    step.status === "running" && "bg-accent/10",
                    isFailed && "bg-red-400/10",
                    isWaiting && "bg-amber-400/10",
                  )}
                  data-testid={`exec-step-${step.index}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                      <StatusIcon status={itemStatus} />
                    </span>
                    <span
                      className={cn(
                        "flex-1 truncate text-[10px]",
                        step.status === "running"
                          ? "font-medium text-primary"
                          : step.status === "completed"
                            ? "text-secondary"
                            : isFailed
                              ? "text-red-400"
                              : isWaiting
                                ? "text-amber-400"
                                : "text-muted",
                      )}
                    >
                      {step.label}
                    </span>
                    {isWaiting && (
                      <span className="flex-shrink-0 text-[9px] text-amber-400">
                        approval
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExecutionSidebar — main component
// ---------------------------------------------------------------------------

export function ExecutionSidebar({
  executionState,
  todos,
  workflowSteps,
  workflowName = "Workflow",
  onRun,
  onStop,
  onApprove,
  onReject,
}: ExecutionSidebarProps) {
  const headerTitle =
    executionState === "running"
      ? "Working..."
      : executionState === "completed"
        ? "Completed"
        : executionState === "failed"
          ? "Failed"
          : executionState === "approval"
            ? "Awaiting approval"
            : "Idle";

  return (
    <div
      className="flex h-full flex-col"
      data-testid="execution-sidebar"
    >
      {/* Header */}
      <div className="flex items-center gap-1.5 px-3 py-2">
        <Icons.Bot className="h-3.5 w-3.5 flex-shrink-0 text-accent" />
        <span className="flex-1 truncate text-[11px] font-semibold text-primary">
          {headerTitle}
        </span>
        <HeaderAction
          executionState={executionState}
          onStop={onStop}
          onRun={onRun}
          onApprove={onApprove}
          onReject={onReject}
        />
      </div>

      {/* Todos section */}
      {todos.length > 0 && <TodosSection todos={todos} />}

      {/* Workflow steps section */}
      {workflowSteps.length > 0 && (
        <WorkflowStepsSection
          steps={workflowSteps}
          workflowName={workflowName}
        />
      )}

      {/* Empty state — no todos and no workflow steps */}
      {todos.length === 0 && workflowSteps.length === 0 && (
        <p className="px-3 py-3 text-center text-[10px] text-muted">
          No activity yet
        </p>
      )}
    </div>
  );
}
